import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

export const faqData = [
  {
    question: "What is The PANTHM Consolidation Architecture (PCA)?",
    answer: "We unify your web applications, mobile apps, and autonomous AI workflows into a single high-performance codebase. This eliminates third-party middleware (like Zapier), data synchronization errors, and compounding subscription fees."
  },
  {
    question: "How do PANTHM's AI Voice Agents work?",
    answer: "Our AI voice agents operate with sub-500ms human-like conversational latency. They autonomously dial leads, qualify prospects, handle objections, and book confirmed meetings directly into your calendar and CRM with zero manual effort."
  },
  {
    question: "Do you support custom CRM and database integrations?",
    answer: "Yes. We engineer direct serverless integrations for HubSpot, Salesforce, Zoho, PostgreSQL, and MongoDB—delivering sub-50ms data sync speeds without relying on fragile third-party connectors."
  },
  {
    question: "How do you ensure compliance and scalability?",
    answer: "All systems feature region-specific database clusters (GDPR compliant), automated edge caching for instant global load times, and elastic serverless backends designed to automatically handle high-traffic spikes seamlessly."
  },
  {
    question: "How long does it take to launch a product with PANTHM AI Labs?",
    answer: "AI Voice Agents launch in 2 to 3 weeks. Production-ready web and mobile MVPs take 3 to 6 weeks, while full enterprise platforms typically deliver in 8 to 12 weeks with milestone tracking."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-slate-100 dark:border-white/10 rounded-2xl bg-white/50 dark:bg-white/5 backdrop-blur-md transition-all duration-300 hover:border-primary/30 dark:hover:border-primary/50 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-6 py-5 flex items-center justify-between text-left gap-4 font-bold text-slate-800 dark:text-slate-100 focus:outline-none"
      >
        <span className="text-base md:text-lg">{question}</span>
        <ChevronDown
          size={20}
          className={`text-slate-400 dark:text-slate-500 transition-transform duration-300 flex-shrink-0 ${
            isOpen ? "transform rotate-180 text-primary" : ""
          }`}
        />
      </button>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-[500px] border-t border-slate-100 dark:border-white/5" : "max-h-0"
        } overflow-hidden`}
      >
        <div className="px-6 py-5 text-sm md:text-base text-slate-700 dark:text-slate-200 leading-relaxed bg-slate-50/30 dark:bg-black/30">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 relative overflow-hidden bg-white dark:bg-[#050505] border-t border-slate-100 dark:border-white/5 noise-overlay">
      {/* Floating ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="wrapper relative z-10 max-w-4xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <h2 data-aos="fade-up" className="section-heading">
            Frequently Asked <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Questions</span>
          </h2>
          <p data-aos="fade-up" data-aos-delay="100" className="text-slate-500 dark:text-slate-400 text-base md:text-lg">
            Understand how our custom architectures and automated systems eliminate operational friction.
          </p>
        </div>

        <div data-aos="fade-up" data-aos-delay="200" className="space-y-4">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
